/**
 * All 18 Colorado Springs landing page configs added in the local-ranking
 * push. Each entry powers one route under src/routes/{slug}.tsx via the
 * shared SpringsLandingPage template.
 *
 * Voice: owner-led, no salespeople, flat pricing, Colorado-based, against the
 * "burned + handed off + nickel-and-dimed" pain point.
 */

import type {
  SpringsLandingConfig,
  SpringsFAQ,
} from "@/components/SpringsLandingPage";
import type { RelatedPageCard } from "@/components/RelatedPagesGrid";

// ===== Shared building blocks =====

const HUB_CARD: RelatedPageCard = {
  to: "/colorado-springs",
  title: "Colorado Springs digital marketing hub",
  description:
    "Every Springs service we offer in one place — SEO, web design, Google Maps, PPC, and GEO AI.",
};

const SEO_HUB_CARD: RelatedPageCard = {
  to: "/colorado-springs-seo",
  title: "Colorado Springs SEO",
  description:
    "Owner-led SEO from our Woodmen Rd office. Map Pack rankings for Springs trades, healthcare, and pro-services.",
};

const GMB_CARD: RelatedPageCard = {
  to: "/colorado-springs-google-maps",
  title: "Colorado Springs Google Maps",
  description:
    "Top-3 Map Pack visibility for Briargate, Falcon, Monument, and Black Forest.",
};

const COMMON_FAQ_OWNER: SpringsFAQ = {
  q: "Will I work directly with the owner?",
  a: "Yes. Every Colorado Springs client works directly with Chris Heidlebaugh. No account managers, no sales reps, no offshore handoff. You get Chris's direct line and an average response time under one hour during business hours.",
};

const COMMON_FAQ_PRICE: SpringsFAQ = {
  q: "What does this cost?",
  a: "Our Growth Marketing package — SEO, Google Maps, GEO AI, and social — is $1,650/month flat. Websites start at $1,850 one-time, owned outright. We publish prices publicly because Springs owners deserve a straight answer instead of a discovery call.",
};

const COMMON_FAQ_LOCAL: SpringsFAQ = {
  q: "Are you actually local to Colorado Springs?",
  a: "Yes. Our office is at 3210 E Woodmen Rd STE 110, Colorado Springs, CO 80920. Verified Google Business Profile. You can stop by, meet Chris, and see the team. We've been a Colorado Springs digital marketing agency since 2006.",
};

// ===== Batch 2 — City × industry SEO combo pages (6) =====

function industrySeoConfig(opts: {
  slug: string;
  industry: string;
  industryLower: string;
  emoji?: string;
  painPoint: string;
  specifics: string;
  bullets: string[];
}): SpringsLandingConfig {
  const { slug, industry, industryLower, painPoint, specifics, bullets } = opts;
  return {
    slug,
    eyebrow: `Colorado Springs ${industry} SEO`,
    title: `Colorado Springs ${industry} SEO — Map Pack Rankings for Springs ${industry} Companies`,
    breadcrumbLabel: `${industry} SEO`,
    heroDescription: `Owner-led SEO for Colorado Springs ${industryLower} companies. Built for Briargate, Powers, Falcon, Monument, and Black Forest — not a generic citywide page.`,
    metaDescription: `Colorado Springs ${industryLower} SEO that wins the Map Pack. Owner-led from our Woodmen Rd office. Flat pricing, no salespeople, no handoffs. Call (719) 419-3935.`,
    ogTitle: `Colorado Springs ${industry} SEO | Owner-Led, Flat Pricing`,
    ogDescription: `SEO built for Colorado Springs ${industryLower} companies. Briargate, Falcon, Monument, Black Forest, and the bases.`,
    intro: `${painPoint} We've been ranking Colorado Springs ${industryLower} companies in the Map Pack since 2006 — owner-led, flat-priced, and headquartered on Woodmen Rd. When you call, Chris answers. No sales pitch, no handoff to a junior account manager three weeks later.`,
    sections: [
      {
        heading: `Why Colorado Springs ${industryLower} SEO is different`,
        body: specifics,
      },
      {
        heading: `What we do for Springs ${industryLower} clients`,
        body: `Every ${industryLower} engagement starts with a free audit — we tell you exactly what's working, what's broken, and what the realistic timeline looks like. No 12-month contract, no setup fee, no surprise invoices.`,
        bullets,
      },
      {
        heading: "Owner-led, no salespeople, flat pricing",
        body: [
          `Most ${industryLower} owners we talk to have been burned by a national agency, pitched and then handed off to someone new every quarter, nickel-and-dimed on every change, and treated like an invoice number instead of a business.`,
          "That's not how we work. You talk to Chris. The price is published. We're in Colorado Springs — not Mumbai, not Manila, not a Manhattan loft. Everything stays month-to-month so we have to earn the next month every month.",
        ],
      },
    ],
    faqs: [
      {
        q: `How long until my ${industryLower} business ranks in the Springs Map Pack?`,
        a: `Most Colorado Springs ${industryLower} categories see meaningful Map Pack movement in 60–90 days. Competitive seasons (peak HVAC, post-hail roofing) can take 4–6 months of consistent review velocity and content. We set realistic timelines on day one — not vanity promises.`,
      },
      COMMON_FAQ_PRICE,
      COMMON_FAQ_OWNER,
      COMMON_FAQ_LOCAL,
    ],
    related: [HUB_CARD, SEO_HUB_CARD, GMB_CARD],
    showCoverageMap: true,
  };
}

const BATCH2: SpringsLandingConfig[] = [
  industrySeoConfig({
    slug: "colorado-springs-hvac-seo",
    industry: "HVAC",
    industryLower: "HVAC",
    painPoint:
      "If you're an HVAC company in Colorado Springs, you're competing with national brands, franchise installers, and Angi/HomeAdvisor pay-to-play farms in every single Map Pack search.",
    specifics:
      "Springs HVAC search has three peaks: pre-summer AC tune-ups (April–May), heat-wave emergencies (July), and pre-winter furnace season (October–November). Each peak has its own keyword cluster, and each Springs neighborhood ranks separately. Briargate searches don't surface the same companies as Old Colorado City searches — even if your service area covers both. We build a dedicated content cluster per neighborhood and per season.",
    bullets: [
      "Neighborhood Map Pack pages (Briargate, Powers, Falcon, Monument, Black Forest)",
      "Seasonal content cadence tied to Springs weather and PCS rotation",
      "Emergency-service schema + speakable answers for voice search",
      "Review-velocity playbook tuned to peak-season install volume",
      "Backlinks from real Colorado HVAC industry sources",
      "Monthly grid-based rank reports — no vanity metrics",
    ],
  }),
  industrySeoConfig({
    slug: "colorado-springs-plumber-seo",
    industry: "Plumber",
    industryLower: "plumbing",
    painPoint:
      "Plumbing in Colorado Springs is dominated by a handful of big-spend competitors plus a constant stream of out-of-area lead aggregators bidding on every emergency keyword.",
    specifics:
      "Springs plumbing search spikes during the first hard freeze (typically mid-October), again during deep freezes in January–February, and around irrigation blow-out season in late September. Burst-pipe and water-heater emergencies don't wait — if you're not in the top 3 of the Map Pack, the caller picks the next number down. We optimize specifically for emergency intent + neighborhood proximity.",
    bullets: [
      "Emergency-plumber Map Pack rankings per neighborhood",
      "Freeze-season + irrigation blow-out content cadence",
      "Service-area pages for Briargate, Powers, Monument, Falcon, Fountain",
      "Schema for 24/7 emergency service + speakable voice search",
      "Backlinks from Colorado plumbing + home-services publications",
      "Call tracking tied to organic + Map Pack source attribution",
    ],
  }),
  industrySeoConfig({
    slug: "colorado-springs-electrician-seo",
    industry: "Electrician",
    industryLower: "electrical",
    painPoint:
      "Electrical contractors in Colorado Springs face the same Angi/HomeAdvisor leak as every other trade — except electrical leads have higher liability, smaller margins on small jobs, and bigger margins on panel upgrades and EV chargers.",
    specifics:
      "Springs electrical search is dominated by panel-upgrade, EV-charger-install, and hot-tub-circuit keywords on the residential side, plus tenant-finish and commercial-rewire on the commercial side. The Map Pack matters most for the high-ticket residential work — homeowners pick from the top 3 photos and reviews. We target those keywords plus the long-tail (\"electrician near me Briargate\", \"EV charger install Monument\") that actually convert.",
    bullets: [
      "Panel-upgrade + EV-charger keyword cluster",
      "Neighborhood-level Map Pack pages",
      "Commercial vs. residential service-area segmentation",
      "Review-generation playbook for high-ticket residential jobs",
      "Backlinks from real Colorado electrical + EV publications",
      "GBP optimization for licensed-electrician trust signals",
    ],
  }),
  industrySeoConfig({
    slug: "colorado-springs-moving-company-seo",
    industry: "Moving Company",
    industryLower: "moving",
    painPoint:
      "Moving companies in Colorado Springs live or die by PCS season — and so do the agencies who chase you with cold calls every February promising they can \"crush Map Pack\" by May.",
    specifics:
      "Springs moving search is uniquely driven by the May–August PCS rotation across Fort Carson, Peterson SFB, Schriever SFB, and the Air Force Academy. There are also constant in-town moves from Briargate buildouts and Powers Corridor apartment turnover. We build content around military-move keywords (DITY, GTCC, household goods) plus local-move keywords, and we time the content cadence so you peak with the season — not after it.",
    bullets: [
      "PCS-season content cadence (DITY moves, GTCC, military discounts)",
      "Local-move keyword cluster for Briargate, Powers, Monument",
      "Review-generation playbook timed to peak season",
      "GBP optimization with military-discount + insured + licensed signals",
      "Backlinks from real Colorado relocation + military publications",
      "Estimate-form optimization tied to organic + Map Pack source",
    ],
  }),
  industrySeoConfig({
    slug: "colorado-springs-concrete-contractor-seo",
    industry: "Concrete Contractor",
    industryLower: "concrete",
    painPoint:
      "Concrete contractors in Colorado Springs compete with a flood of out-of-area bidders every spring, and most owners we talk to have been burned by an agency that promised rankings and delivered a slow website and a vanity report.",
    specifics:
      "Springs concrete search is highly seasonal — driveway, patio, and stamped-concrete searches spike from April through September, then drop hard. Commercial slab and tilt-up search is steadier but smaller. The Map Pack rewards proximity, photos, and review velocity, and Briargate / Powers / Black Forest each have their own pack. We build seasonal content + a neighborhood Map Pack strategy that compounds year over year.",
    bullets: [
      "Driveway + patio + stamped-concrete keyword cluster",
      "Neighborhood Map Pack pages (Briargate, Powers, Black Forest, Monument)",
      "Photo-asset playbook for GBP and on-page",
      "Review-generation cadence timed to peak install season",
      "Backlinks from real Colorado construction publications",
      "Commercial vs. residential service-area segmentation",
    ],
  }),
  industrySeoConfig({
    slug: "colorado-springs-auto-repair-seo",
    industry: "Auto Repair",
    industryLower: "auto repair",
    painPoint:
      "Auto-repair shops in Colorado Springs are squeezed by dealership service departments on one side and big-box quick-lube chains on the other — and the agencies that pitched you a \"Map Pack package\" almost always handed your account to someone in another state.",
    specifics:
      "Springs auto-repair search is driven by proximity. Drivers pick from the top 3 Map Pack results within ~2 miles of where they live or work. That means a single citywide page won't rank — you need a Briargate page, a Powers page, a Falcon page, and so on. We also build review-generation around the moments customers are most willing to leave a review (immediately after vehicle pickup, while still on premises).",
    bullets: [
      "Neighborhood Map Pack pages (Briargate, Powers, Falcon, Northgate)",
      "Review-generation playbook timed to vehicle pickup",
      "Service-category content (brakes, alignment, transmission, AC)",
      "GBP optimization with shop-photos + ASE-certified trust signals",
      "Backlinks from real Colorado automotive publications",
      "Call tracking tied to organic + Map Pack source attribution",
    ],
  }),
];

// ===== Batch 3 — Neighborhood landing pages (6) =====

function neighborhoodConfig(opts: {
  slug: string;
  name: string;
  zips: string[];
  driveTime: string;
  landmarks: string;
  topIndustries: string;
}): SpringsLandingConfig {
  const { slug, name, zips, driveTime, landmarks, topIndustries } = opts;
  return {
    slug,
    eyebrow: `${name} SEO`,
    title: `${name} SEO — Colorado Springs Map Pack for ${name} Businesses`,
    breadcrumbLabel: `${name} SEO`,
    heroDescription: `Owner-led SEO for ${name} businesses in Colorado Springs. ${driveTime} from our Woodmen Rd office. Built specifically for the ${name} Map Pack — not a generic citywide page.`,
    metaDescription: `${name} SEO from a Colorado Springs agency that actually lives here. Flat pricing, owner-led, no handoffs. ZIPs ${zips.join(", ")}. Call (719) 419-3935.`,
    ogTitle: `${name} SEO | Owner-Led Colorado Springs Agency`,
    ogDescription: `Map Pack rankings built specifically for ${name} — not a generic Colorado Springs page.`,
    intro: `${name} has its own Google Map Pack — separate from downtown Colorado Springs, separate from Powers, separate from every other neighborhood. We've been building dedicated ${name} content + GBP optimization for clients since 2006. We're ${driveTime} away on Woodmen Rd. When you call, Chris answers.`,
    sections: [
      {
        heading: `Why ${name} ranks differently than the rest of Colorado Springs`,
        body: `Google serves the Map Pack based on the searcher's location — not the business's. A homeowner searching from ${landmarks} sees a completely different top-3 than someone searching from Old Colorado City. Most agencies build one citywide page and wonder why the ${name} Map Pack doesn't move. We build a dedicated ${name} page, with ${name}-specific schema, ${name} landmarks in the content, and a backlink + review strategy aimed at the ${name} ZIPs (${zips.join(", ")}).`,
      },
      {
        heading: `Industries we rank in ${name}`,
        body: `Top categories we've worked on in ${name}: ${topIndustries}. The play is the same regardless of industry — dedicated neighborhood page, GBP service-area tuning, review velocity from real local customers, and backlinks from real Colorado sources.`,
      },
      {
        heading: "Owner-led, flat pricing, headquartered on Woodmen Rd",
        body: [
          `Most ${name} owners we talk to have been pitched by a national agency, handed off after the sale, charged for every change request, and then ghosted when results stalled.`,
          "We're the opposite. You talk to Chris. Prices are published. Everything is month-to-month. Our office is on Woodmen Rd — you can drive over.",
        ],
      },
    ],
    zips,
    faqs: [
      {
        q: `Do you actually work with businesses in ${name}?`,
        a: `Yes — ${name} is one of the neighborhoods we work in most. Our Woodmen Rd office is ${driveTime} from ${name}, and we've built dedicated ${name} content + GBP strategies for clients in ZIPs ${zips.join(", ")}.`,
      },
      {
        q: `Why does ${name} need its own SEO page?`,
        a: `Because Google serves the Map Pack based on searcher location. A single citywide \"Colorado Springs\" page won't rank for ${name} searches — Google rewards proximity, neighborhood-specific content, and GBP signals tuned to the actual service area. A dedicated ${name} page wins the ${name} pack.`,
      },
      COMMON_FAQ_PRICE,
      COMMON_FAQ_OWNER,
    ],
    related: [HUB_CARD, SEO_HUB_CARD, GMB_CARD],
    showCoverageMap: true,
  };
}

const BATCH3: SpringsLandingConfig[] = [
  neighborhoodConfig({
    slug: "colorado-springs-seo-briargate",
    name: "Briargate",
    zips: ["80920", "80921", "80923", "80924"],
    driveTime: "5 minutes",
    landmarks: "the Promenade Shops, Pine Creek, or Chapel Hills Mall",
    topIndustries:
      "HVAC, dental practices, med-spas, home remodelers, auto repair, and family law firms",
  }),
  neighborhoodConfig({
    slug: "colorado-springs-seo-powers",
    name: "Powers Corridor",
    zips: ["80915", "80916", "80917", "80922", "80923"],
    driveTime: "10 minutes",
    landmarks: "First & Main Town Center, Powers Crossing, or the Powers/Galley intersection",
    topIndustries:
      "auto repair, plumbing, HVAC, urgent care, restaurants, and apartment-services contractors",
  }),
  neighborhoodConfig({
    slug: "colorado-springs-seo-northgate",
    name: "Northgate",
    zips: ["80921", "80920", "80924"],
    driveTime: "10 minutes",
    landmarks: "the Air Force Academy north gate, Interquest, or InterQuest Marketplace",
    topIndustries:
      "moving companies (military PCS), real estate, financial services, dental, and home remodelers",
  }),
  neighborhoodConfig({
    slug: "colorado-springs-seo-black-forest",
    name: "Black Forest",
    zips: ["80908", "80921", "80831"],
    driveTime: "15 minutes",
    landmarks: "the Black Forest Regional Park, Shoup Rd, or Black Forest Rd & Burgess",
    topIndustries:
      "well/septic, propane, fence + gate contractors, custom home builders, equestrian services, and tree work",
  }),
  neighborhoodConfig({
    slug: "colorado-springs-seo-monument",
    name: "Monument",
    zips: ["80132", "80133"],
    driveTime: "20 minutes",
    landmarks: "downtown Monument, Jackson Creek, or the Monument exit off I-25",
    topIndustries:
      "HVAC, custom home builders, dental, med-spas, financial services, and real estate",
  }),
  neighborhoodConfig({
    slug: "colorado-springs-seo-falcon",
    name: "Falcon",
    zips: ["80831", "80908"],
    driveTime: "20 minutes",
    landmarks: "Falcon High School, Meridian Ranch, or the Woodmen/Meridian intersection",
    topIndustries:
      "roofing, well/septic, fence contractors, propane, HVAC, and custom home builders",
  }),
];

// ===== Batch 4 — Lead-magnet / authority pages (4) =====

const BATCH4: SpringsLandingConfig[] = [
  {
    slug: "colorado-springs-google-business-profile-playbook",
    eyebrow: "GBP Playbook",
    title: "Colorado Springs Google Business Profile Playbook (2026 Edition)",
    breadcrumbLabel: "GBP Playbook",
    heroDescription:
      "Everything we do to rank Colorado Springs clients in the Map Pack — categories, services, posts, Q&A, photos, and review cadence. No paywall, no email gate.",
    metaDescription:
      "The exact Google Business Profile playbook we use to rank Colorado Springs clients in the Map Pack. Categories, services, posts, photos, reviews. Free, no email gate.",
    intro:
      "Most Colorado Springs businesses we talk to are leaving 70–80% of their Map Pack potential on the table because their Google Business Profile is half-configured. This playbook is the exact step-by-step we use to rank Springs HVAC, dental, law, and home-services clients. It's free. There's no email gate. If you want help implementing it, call Chris — but you don't have to.",
    schemaType: "Article",
    sections: [
      {
        heading: "Step 1 — Primary + secondary categories",
        body: "Your primary category is the single biggest GBP ranking factor. Pick the most specific match, then add up to 9 secondary categories that describe what you actually do. Don't over-stuff — Google ignores irrelevant categories and may flag the profile.",
        bullets: [
          "Pick the most specific primary category (e.g. \"HVAC Contractor\" not \"Contractor\")",
          "Add 3–9 relevant secondary categories",
          "Audit every quarter — Google adds new categories regularly",
          "Match the primary category exactly to the dominant keyword you want to rank for",
        ],
      },
      {
        heading: "Step 2 — Services + service-area configuration",
        body: "Add every individual service as a separate line item with a 1–2 sentence description. This is what fills the \"Services\" tab on your profile and powers the service-keyword Map Pack rankings. Then configure your service area — for most Springs businesses we recommend a 10-mile radius, not a list of cities (cities cap at 20 and dilute the signal).",
      },
      {
        heading: "Step 3 — Posts, Q&A, and photo cadence",
        body: "Post weekly to your GBP — offers, updates, or events. Seed your own Q&A with the 8–10 questions customers actually ask (then mark them as the business answer). Upload 5–10 photos per month with descriptive filenames + geotag-friendly content (job photos in identifiable Springs locations beat stock).",
      },
      {
        heading: "Step 4 — Review velocity + response",
        body: [
          "Reviews are the second-biggest Map Pack signal after primary category. Aim for 2–4 new reviews per month consistently. We use a simple text+email request triggered at job completion, then respond to every review (good or bad) within 48 hours.",
          "Critical: never offer incentives in exchange for reviews. Google catches this and will suppress the profile.",
        ],
      },
      {
        heading: "Step 5 — Monthly audit",
        body: "Once a month, check your GBP Insights for the search queries Google is showing you for, your photo views vs. competitors, and any unanswered Q&A. We do this for every client on the 1st of the month — and we'll do the first one for free if you want a second opinion.",
      },
    ],
    faqs: [
      {
        q: "Can I do all of this myself?",
        a: "Yes — and many Colorado Springs business owners do. The playbook above is everything we do for paying clients, just laid out. It takes about 4–6 hours to set up properly and 1–2 hours per month to maintain.",
      },
      {
        q: "What if I want help?",
        a: "GBP management is included in our Growth Marketing package ($1,650/month). It's also available standalone — call Chris at (719) 419-3935 for a quote.",
      },
      COMMON_FAQ_OWNER,
      COMMON_FAQ_LOCAL,
    ],
    related: [HUB_CARD, GMB_CARD, SEO_HUB_CARD],
    showCoverageMap: false,
  },
  {
    slug: "colorado-springs-citation-audit",
    eyebrow: "NAP & Citation Audit",
    title: "Colorado Springs NAP + Citation Audit (Free Checklist)",
    breadcrumbLabel: "Citation Audit",
    heroDescription:
      "Inconsistent business name, address, or phone (NAP) across the web is the #1 silent killer of Colorado Springs Map Pack rankings. Use this checklist to audit yours.",
    metaDescription:
      "Free Colorado Springs NAP + citation audit checklist. Find and fix the inconsistent listings that are silently killing your Map Pack rankings.",
    intro:
      "When you change your address, your phone number, or your business name, Google looks at every citation across the web to decide which version is correct. If there are 30 versions of your old phone number floating around, Google trusts none of them — and your Map Pack ranking drops. This audit is what we run for every new Colorado Springs client.",
    schemaType: "Article",
    sections: [
      {
        heading: "Step 1 — Pick your canonical NAP",
        body: "Decide the exact spelling, abbreviation, and format you'll use everywhere. \"3210 E Woodmen Rd STE 110\" is different from \"3210 East Woodmen Road, Suite 110\" — Google treats them as different addresses. Pick one. Document it. Use it on every listing.",
        bullets: [
          "Business name: exact spelling, no descriptors (\"Acme HVAC\" not \"Acme HVAC Colorado Springs\")",
          "Address: pick a format and stick to it everywhere",
          "Phone: one primary number, formatted the same way (e.g. (719) 419-3935)",
          "Website: pick https vs. http and www vs. apex, redirect the rest",
        ],
      },
      {
        heading: "Step 2 — Audit the top 30 citation sources",
        body: "Check your listing on each of these and verify NAP matches exactly: Google Business Profile, Bing Places, Apple Business Connect, Facebook, Yelp, BBB, Angi, HomeAdvisor, Thumbtack, Houzz, Nextdoor, Yellowpages, Foursquare, Manta, MapQuest, Local.com, Citysearch, Superpages, Chamber of Commerce (Colorado Springs Chamber + Pikes Peak), industry-specific directories.",
      },
      {
        heading: "Step 3 — Fix inconsistencies",
        body: "For each citation, either claim and update the listing yourself, or contact support. The big ones (Google, Bing, Apple, Facebook, Yelp) you must own and update directly. The smaller ones can be batch-updated through tools like Whitespark, BrightLocal, or Moz Local — but a manual update of the top 30 catches 90% of the impact.",
      },
      {
        heading: "Step 4 — Monitor monthly",
        body: "Once a month, search your business name + phone in Google. Look for any old NAP variants that resurface (data aggregators repopulate old data). Fix them as they appear.",
      },
    ],
    faqs: [
      {
        q: "How long until my Map Pack ranking recovers?",
        a: "Typically 30–60 days after the major citations are fixed. Google needs time to recrawl and reconcile the changes.",
      },
      {
        q: "Do you offer this as a service?",
        a: "Yes — citation cleanup is included in our Growth Marketing package. Standalone audits start at $499 one-time. Call Chris at (719) 419-3935.",
      },
      COMMON_FAQ_PRICE,
      COMMON_FAQ_OWNER,
    ],
    related: [HUB_CARD, GMB_CARD, SEO_HUB_CARD],
    showCoverageMap: false,
  },
  {
    slug: "colorado-springs-review-generation",
    eyebrow: "Review Generation",
    title: "Colorado Springs Review Generation — Get More 5-Star Google Reviews",
    breadcrumbLabel: "Review Generation",
    heroDescription:
      "Reviews are the #2 Map Pack ranking signal after primary category. Here's the exact system we use to get Colorado Springs clients 2–4 new 5-star reviews per month.",
    metaDescription:
      "How we get Colorado Springs clients 2–4 new 5-star Google reviews per month. Templates, timing, and the playbook we built from an auto-repair case study.",
    intro:
      "An auto-repair client of ours in the Springs went from 47 reviews to 312 in 14 months — and from outside the Map Pack to #1 for their primary keyword — using nothing but a simple text-based review request triggered at the moment of vehicle pickup. This page is the playbook.",
    schemaType: "Article",
    sections: [
      {
        heading: "Step 1 — Pick the right moment",
        body: "Ask for the review when the customer is most emotionally engaged — usually within 5 minutes of job completion. For an auto-repair shop, that's vehicle pickup. For HVAC, it's right after the install passes inspection. For dental, it's at checkout while they're scheduling the next visit. The longer you wait, the lower the conversion.",
      },
      {
        heading: "Step 2 — Use SMS, not email",
        body: "SMS review requests convert 5–8x higher than email. Send a single text with the customer's first name, a thank you, and a direct link to your Google review form (use your GBP short URL — g.page/r/...). Don't include other links. Don't include a survey. One ask, one link.",
      },
      {
        heading: "Step 3 — Respond to every review within 48 hours",
        body: "Google explicitly rewards profiles with high response rates. Respond to every review — 5-star and 1-star — with a personalized, non-template message. Use the customer's name. Reference the specific service. Keep it under 4 sentences.",
      },
      {
        heading: "Step 4 — Recover bad reviews professionally",
        body: [
          "When a 1-star or 2-star review lands, do not respond in anger. Respond publicly with empathy + a path to resolution (\"I'm sorry that happened — please call me directly at...\"), then resolve privately. Customers often update the review after the issue is fixed.",
          "Critical: never offer discounts, gift cards, or anything of value in exchange for removing or updating a review. Google catches this.",
        ],
      },
    ],
    faqs: [
      {
        q: "What tool do you use for SMS review requests?",
        a: "For most Springs clients we set up NiceJob, Birdeye, or a simple Zapier + Twilio flow tied to their CRM. The tool matters less than the timing.",
      },
      {
        q: "Can you set this up for me?",
        a: "Yes — review generation is included in our Growth Marketing package. Standalone setup is $499 one-time. Call Chris at (719) 419-3935.",
      },
      COMMON_FAQ_OWNER,
      COMMON_FAQ_LOCAL,
    ],
    related: [HUB_CARD, GMB_CARD, SEO_HUB_CARD],
    showCoverageMap: false,
  },
  {
    slug: "colorado-springs-local-link-building",
    eyebrow: "Local Link Building",
    title: "Colorado Springs Local Link Building — Real Backlinks, No PBNs",
    breadcrumbLabel: "Local Link Building",
    heroDescription:
      "The local Colorado partnerships, chambers, sponsorships, and publications we use to build real backlinks for Colorado Springs clients — no PBNs, no link farms.",
    metaDescription:
      "How we build real local backlinks for Colorado Springs clients — Chamber, BBB, sponsorships, partnerships, and publications. No PBNs, no link farms.",
    intro:
      "Google's algorithm has gotten ruthless about low-quality backlinks. The agencies still selling \"500 backlinks for $99\" are actively hurting their clients. The links that move Colorado Springs rankings now are real, local, and earned — Chamber memberships, BBB accreditation, charity sponsorships, local press, and partnerships with non-competing Springs businesses.",
    schemaType: "Article",
    sections: [
      {
        heading: "Tier 1 — The non-negotiables",
        body: "Every Colorado Springs business should have these from day one. They're cheap, they last forever, and they signal local trust to Google.",
        bullets: [
          "Colorado Springs Chamber of Commerce membership ($475/yr — direct dofollow link)",
          "Pikes Peak Small Business Development Center listing (free)",
          "Better Business Bureau (BBB) accreditation",
          "Visit Colorado Springs business listing (if applicable)",
          "Industry-specific Colorado association (e.g. Colorado HVAC Council, Colorado Bar Association)",
        ],
      },
      {
        heading: "Tier 2 — Sponsorships + charity",
        body: "Pick 1–2 local sponsorships per year. The link matters less than the local relevance — sponsoring a Briargate little-league team or a Pikes Peak charity 5K gets you a dofollow link AND ties your brand to a real Springs cause.",
        bullets: [
          "Local school sports teams (most have a sponsor page)",
          "Pikes Peak Run + Pikes Peak Marathon sponsorships",
          "Colorado Springs Food Pantry / Care & Share partnerships",
          "Military family non-profits (Operation Homefront, USO, etc.)",
        ],
      },
      {
        heading: "Tier 3 — Local press + publications",
        body: "Pitch one local story per quarter to Gazette.com, KOAA, Colorado Springs Business Journal, and Pikes Peak Bulletin. The story doesn't have to be about your business — community contributions, free seminars, or industry commentary all earn coverage.",
      },
      {
        heading: "Tier 4 — Partnerships with non-competing locals",
        body: "Build cross-referral relationships with 5–10 non-competing Springs businesses (e.g. an HVAC company partners with a plumber, an electrician, and an insulation contractor). Trade links from your \"Trusted Partners\" page. Real local relevance — and a steady referral stream.",
      },
    ],
    faqs: [
      {
        q: "How many backlinks do I need?",
        a: "Quality dwarfs quantity. 20–40 real local backlinks from the tiers above will outrank 500 PBN links every time. We aim for 2–4 new high-quality local links per month for ongoing clients.",
      },
      {
        q: "Do you do this for me?",
        a: "Yes — local link building is included in our Growth Marketing package. We do the Chamber, BBB, sponsorship, and partnership outreach for you. Call Chris at (719) 419-3935.",
      },
      COMMON_FAQ_PRICE,
      COMMON_FAQ_OWNER,
    ],
    related: [HUB_CARD, SEO_HUB_CARD, GMB_CARD],
    showCoverageMap: false,
  },
];

// ===== Batch 5 — Proof + answer surface (2) =====

const BATCH5: SpringsLandingConfig[] = [
  {
    slug: "colorado-springs-seo-results",
    eyebrow: "Real Results",
    title: "Colorado Springs SEO Results — Anonymized Rank Movements",
    breadcrumbLabel: "Results",
    heroDescription:
      "Anonymized rank-tracker data from real Colorado Springs clients. No vanity metrics, no cherry-picked screenshots — just before/after on the keywords that drive revenue.",
    metaDescription:
      "Anonymized SEO results from real Colorado Springs clients. Before/after rank movements on the keywords that actually drive revenue. No vanity metrics.",
    intro:
      "Every agency screenshots their best month. We're going to show you the actual rank movement across our Colorado Springs client base — anonymized for privacy but real numbers from real Springs businesses. If you want a reference call with the underlying client (with their permission), Chris can usually arrange it.",
    sections: [
      {
        heading: "Springs HVAC — Briargate-area client",
        body: [
          "Starting state: outside top 20 for primary keyword \"hvac repair colorado springs\". Map Pack: not visible.",
          "After 6 months: organic position #4, Map Pack #2 from Briargate, Powers, and Northgate ZIPs. Organic-attributed calls up ~140% year-over-year.",
        ],
      },
      {
        heading: "Springs roofing — Falcon-area client",
        body: [
          "Starting state: organic position #14 for primary keyword. Map Pack: #6 from Falcon, not visible from Briargate.",
          "After 9 months (post-hail season included): organic #2, Map Pack #1 from Falcon, #3 from Briargate. Estimate-form submissions up ~210%.",
        ],
      },
      {
        heading: "Springs dental — Monument-area client",
        body: [
          "Starting state: organic position #22 for \"dentist monument co\". Map Pack: outside top 3.",
          "After 5 months: organic #1, Map Pack #1 from Monument and Tri-Lakes ZIPs. New-patient calls up ~85% YoY.",
        ],
      },
      {
        heading: "What we don't show on this page",
        body: "We don't publish vanity metrics — keyword counts, impressions, or \"traffic up 400%\" charts that don't tie to revenue. If you want to see the full client name, the underlying GA4 / GBP Insights screenshots, and (in some cases) a reference call, that's a 15-minute conversation with Chris — not a paid discovery call.",
      },
    ],
    faqs: [
      {
        q: "Why are these anonymized?",
        a: "Most of our clients prefer not to broadcast their SEO strategy to competitors. We can usually share the full details (including a reference call) one-on-one with serious prospects — call Chris at (719) 419-3935.",
      },
      {
        q: "How long until I see results like these?",
        a: "Most Colorado Springs categories see meaningful Map Pack movement in 60–90 days, with the bigger lifts (top-3 organic + top-3 Map Pack) typically landing in months 5–9. Highly competitive verticals can take longer.",
      },
      COMMON_FAQ_PRICE,
      COMMON_FAQ_OWNER,
    ],
    related: [HUB_CARD, SEO_HUB_CARD, GMB_CARD],
    showCoverageMap: true,
  },
  {
    slug: "colorado-springs-seo-faq",
    eyebrow: "Springs SEO FAQ",
    title: "Colorado Springs SEO FAQ — 18 Questions Springs Owners Actually Ask",
    breadcrumbLabel: "Springs SEO FAQ",
    heroDescription:
      "Straight answers to the SEO questions Colorado Springs business owners ask us every week. No fluff, no upsell — just what we'd tell a friend over coffee.",
    metaDescription:
      "Straight answers to the Colorado Springs SEO questions owners actually ask — pricing, timelines, Map Pack, GBP, reviews, agencies, and more. Owner-led.",
    intro:
      "We get the same 18 questions almost every week from Colorado Springs business owners. Here are the answers we give — in plain English, with no sales pitch attached. If you don't see your question, call Chris at (719) 419-3935 and we'll add it.",
    schemaType: "FAQPage",
    sections: [],
    faqs: [
      {
        q: "How long until I rank in the Colorado Springs Map Pack?",
        a: "Most Springs categories see meaningful Map Pack movement in 60–90 days. Competitive verticals (HVAC during summer, roofing post-hail) can take 4–6 months of consistent review velocity and content work.",
      },
      {
        q: "What does Colorado Springs SEO cost?",
        a: "Our Growth Marketing package — SEO, Google Maps, GEO AI, and social — is $1,650/month flat. SEO-only standalone starts at $1,200/month. Websites start at $1,850 one-time, owned outright.",
      },
      {
        q: "Do you have a contract?",
        a: "No long-term contract. Everything is month-to-month. We have to earn the next month every month — that's the whole point.",
      },
      {
        q: "Why isn't my business showing in the Map Pack?",
        a: "Usually one of four things: wrong primary GBP category, inconsistent NAP across the web, not enough recent reviews, or no neighborhood-level content. We can usually diagnose in 15 minutes on a call.",
      },
      {
        q: "Does my Briargate business need a separate page from my Powers business?",
        a: "If you serve both, yes — Google serves the Map Pack based on the searcher's location, and Briargate and Powers have separate packs. A single citywide page won't rank in both.",
      },
      {
        q: "Will you build my website too?",
        a: "Yes. Websites start at $1,850 one-time, owned outright (no monthly fee). Built on a stack you can edit yourself. Hosting is $50/month flat if you want us to handle it.",
      },
      {
        q: "Do you do PPC + SEO together?",
        a: "Yes — they work better together. PPC fills the gap while SEO compounds, and the keyword data from PPC informs the SEO content cadence.",
      },
      {
        q: "What's GEO AI / generative engine optimization?",
        a: "Getting your business cited when locals ask ChatGPT, Gemini, or Perplexity for a Colorado Springs business. It's a different signal stack than traditional SEO — schema, citations, and structured content matter more than backlinks.",
      },
      {
        q: "How is what you do different from BrightLocal / Yext / Birdeye?",
        a: "Those are tools — they automate one piece (citations, reviews, listings). We're the operator who decides what to do, executes the strategy, and ties it all to revenue. We use some of those tools internally for clients.",
      },
      {
        q: "Will I work directly with the owner?",
        a: COMMON_FAQ_OWNER.a,
      },
      {
        q: "Are you actually local to Colorado Springs?",
        a: COMMON_FAQ_LOCAL.a,
      },
      {
        q: "Why are most agencies so bad at this?",
        a: "Most agencies are pitched-and-handed-off shops. The salesperson closes you, then your account goes to a junior account manager juggling 40 other clients in another state. We don't do that — Chris does the work.",
      },
      {
        q: "Do I need to pay extra for content?",
        a: "No. Content is included in the Growth Marketing package — we write 4–6 pieces per month in your voice. No surprise per-article fees.",
      },
      {
        q: "Do I need to pay extra for changes to my GBP / website?",
        a: "No. Changes are included. We don't bill hourly for routine work.",
      },
      {
        q: "How many reviews do I need?",
        a: "More than your top 3 competitors in the Map Pack, with a recent cadence (2–4 per month). Volume matters but recency matters more.",
      },
      {
        q: "Do you guarantee #1 rankings?",
        a: "No. Anyone who guarantees rankings is either lying or about to get penalized. We guarantee process, communication, and effort — and we publish our actual client rank movements.",
      },
      {
        q: "What if I want to cancel?",
        a: "Just call or email — there's no cancellation form, no exit fee, no clawback. You own your website, your content, and your GBP. We don't hold anything hostage.",
      },
      {
        q: "How do I get started?",
        a: "Call Chris at (719) 419-3935 or use the contact form. The first conversation is 15 minutes, free, no pitch — just a straight assessment of where you are and what's realistic.",
      },
    ],
    related: [HUB_CARD, SEO_HUB_CARD, GMB_CARD],
    showCoverageMap: false,
  },
];

// ===== Batch 6 — Additional industry combos (6) =====

const BATCH6_INDUSTRIES: SpringsLandingConfig[] = [
  industrySeoConfig({
    slug: "colorado-springs-garage-door-seo",
    industry: "Garage Door",
    industryLower: "garage door",
    painPoint:
      "Garage door companies in Colorado Springs compete with franchise networks and out-of-area lead aggregators bidding every emergency keyword — and most agencies who pitched you have never worked the Springs hail or cold-snap seasons.",
    specifics:
      "Springs garage door search has two huge spikes: hail-storm aftermath (June–August), when bent panels and broken openers swamp call volume, and the first hard freeze (October–November) when springs snap. Emergency intent dominates — the caller picks from the top 3 Map Pack results within minutes. Briargate, Powers, Black Forest, and Monument each have their own pack. We build a dedicated neighborhood page + emergency-service schema per ZIP.",
    bullets: [
      "Emergency garage door Map Pack pages per neighborhood",
      "Hail-season + freeze-season content cadence",
      "Service-area pages for Briargate, Powers, Monument, Black Forest, Falcon",
      "Schema for 24/7 emergency service + broken-spring speakable answers",
      "Review-generation playbook timed to job completion on-truck",
      "Backlinks from real Colorado home-services publications",
    ],
  }),
  industrySeoConfig({
    slug: "colorado-springs-painter-seo",
    industry: "Painter",
    industryLower: "painting",
    painPoint:
      "Painting contractors in Colorado Springs face fierce seasonal competition — every spring brings a wave of out-of-state crews chasing the same exterior repaint searches you depend on for the year.",
    specifics:
      "Springs painting search is sharply seasonal: exterior repaints peak May–September, interior projects fill the shoulder months (October–April). HOA-managed neighborhoods (Briargate, Cordera, Banning Lewis) require color-approval expertise that out-of-area crews don't have. We build a dedicated content cluster for HOA-friendly painters, plus neighborhood Map Pack pages and a review cadence timed to project walkthroughs.",
    bullets: [
      "Exterior vs. interior keyword cluster segmentation",
      "HOA-approved painter content + neighborhood-specific pages",
      "Cabinet refinishing + commercial painting service-area pages",
      "Photo-asset playbook (before/after) for GBP + on-page",
      "Review-generation timed to project walkthrough",
      "Backlinks from real Colorado design + remodel publications",
    ],
  }),
  industrySeoConfig({
    slug: "colorado-springs-landscaper-seo",
    industry: "Landscaper",
    industryLower: "landscaping",
    painPoint:
      "Landscaping owners in Colorado Springs get the worst of both worlds — Angi/HomeAdvisor siphoning every lead off the top, plus mowing-only operators flooding the Map Pack with no-website listings during peak season.",
    specifics:
      "Springs landscape search splits into three buckets: design/install (peak April–June), maintenance contracts (signed in March), and snow removal (October bookings). Each has its own keyword cluster. The Map Pack rewards visual proof — drone photos, plant-zone-appropriate portfolios, and review velocity from real Springs neighborhoods. We build a portfolio + neighborhood Map Pack strategy that compounds across all three seasons.",
    bullets: [
      "Design/install vs. maintenance vs. snow-removal segmentation",
      "Drone-photography + plant-zone-appropriate portfolio play",
      "Service-area pages for Briargate, Black Forest, Monument, Powers",
      "Review-generation playbook timed to project completion",
      "GBP optimization with native-Colorado plant + xeriscape signals",
      "Backlinks from real Colorado horticulture + HOA publications",
    ],
  }),
  industrySeoConfig({
    slug: "colorado-springs-chiropractor-seo",
    industry: "Chiropractor",
    industryLower: "chiropractic",
    painPoint:
      "Chiropractic practices in Colorado Springs compete with corporate clinic chains, integrated wellness centers, and a constant stream of \"medical SEO\" agencies that don't actually understand local healthcare buying behavior.",
    specifics:
      "Chiropractic search in the Springs is recurring-revenue gold — once a patient finds you, lifetime value is high. The Map Pack matters disproportionately because new patients search by proximity (\"chiropractor near me\") and pick from the top 3 photos + reviews. Military families on PCS rotation make up a meaningful share — they search the moment they arrive at Fort Carson, Peterson, or the Academy. We build a neighborhood + military-friendly content strategy.",
    bullets: [
      "Neighborhood Map Pack pages (Briargate, Powers, Monument, Northgate)",
      "Military-PCS-friendly content (TRICARE, VA, new-to-Springs)",
      "Condition-specific landing pages (sciatica, auto injury, pediatric)",
      "Review-generation timed to first adjustment completion",
      "GBP optimization with licensed-DC + technique-specific signals",
      "Backlinks from real Colorado health + wellness publications",
    ],
  }),
  industrySeoConfig({
    slug: "colorado-springs-med-spa-seo",
    industry: "Med Spa",
    industryLower: "med spa",
    painPoint:
      "Med spas in Colorado Springs face the highest-ticket competition of any local service — franchise spas, dermatologist-led practices, and national chains all bidding on the same Botox + filler keywords.",
    specifics:
      "Springs med spa search is dominated by treatment-specific keywords (Botox, fillers, laser hair removal, microneedling, weight-loss injections) and the buying decision is photo-driven. The Map Pack is critical — searchers pick from the top 3 results with the best portfolio photos and most recent 5-star reviews. Briargate, Northgate, and Monument have separate packs. We build a treatment-specific + neighborhood content strategy with heavy emphasis on before/after asset velocity.",
    bullets: [
      "Treatment-specific landing pages (Botox, fillers, laser, GLP-1)",
      "Neighborhood Map Pack pages (Briargate, Northgate, Monument)",
      "Before/after photo-asset playbook for GBP + on-page",
      "Review-generation timed to follow-up appointment",
      "GBP optimization with licensed-provider + brand-name signals",
      "Backlinks from real Colorado aesthetics + wellness publications",
    ],
  }),
  industrySeoConfig({
    slug: "colorado-springs-roofer-seo",
    industry: "Roofer",
    industryLower: "roofing",
    painPoint:
      "Roofers in Colorado Springs face the most chaotic SEO landscape in the state — every hailstorm brings a wave of out-of-state storm chasers bidding every emergency roofing keyword for the next 90 days.",
    specifics:
      "Springs roofing search is dictated by hail. After a hail event, search volume for 'roof repair Colorado Springs', 'hail damage roofer', and 'insurance roofer near me' spikes 5–10x for weeks. Out-of-state storm chasers flood Google Ads and citations; only local roofers with deep on-page authority and real Springs reviews survive long-term. Briargate, Black Forest, Monument, and Falcon each have separate Map Packs. We build storm-season content, insurance-claim landing pages, and neighborhood Map Pack pages that compound year over year.",
    bullets: [
      "Hail-season content cadence + insurance-claim landing pages",
      "Neighborhood Map Pack pages (Briargate, Black Forest, Monument, Falcon, Powers)",
      "Storm-chaser-resistant on-page authority (real Springs photos, real owners)",
      "Review-generation playbook timed to job completion and inspection",
      "GBP optimization with GAF/Owens-Corning certification signals",
      "Backlinks from real Colorado home + insurance publications",
    ],
  }),
  industrySeoConfig({
    slug: "colorado-springs-handyman-seo",
    industry: "Handyman",
    industryLower: "handyman",
    painPoint:
      "Handymen in Colorado Springs lose half their lead flow to Angi and Thumbtack scraping every search result — and the other half to no-website operators flooding the Map Pack with 5-star reviews from family.",
    specifics:
      "Springs handyman search is heavily proximity-based. 'Handyman near me' is one of the highest-intent local searches, and the Map Pack winners get most of the calls. Out-of-state lead aggregators dominate the top of search, but a tuned GBP + real neighborhood pages can leapfrog them in the Pack. Briargate, Powers, Monument, and Black Forest each have separate packs. We build a service-specific content cluster (drywall, painting, fence repair, deck repair) plus neighborhood Map Pack pages.",
    bullets: [
      "Service-specific landing pages (drywall, fence, deck, painting, mounting)",
      "Neighborhood Map Pack pages (Briargate, Powers, Monument, Black Forest)",
      "Pricing-transparency content that beats Angi/Thumbtack on intent",
      "Review-generation playbook timed to project completion",
      "GBP optimization with licensed + insured + bonded signals",
      "Backlinks from real Colorado home + remodel publications",
    ],
  }),
  industrySeoConfig({
    slug: "colorado-springs-locksmith-seo",
    industry: "Locksmith",
    industryLower: "locksmith",
    painPoint:
      "Locksmiths in Colorado Springs fight one of the most fraud-heavy local search categories on the internet — fake nationwide locksmiths spoofing local addresses and undercutting every real Springs operator on price.",
    specifics:
      "Springs locksmith search is dominated by emergency intent ('locked out of car', 'house lockout', '24-hour locksmith'). Google has been actively de-listing fake locksmith spam, which means a properly verified GBP with real address verification, real reviews, and real on-page content can dominate the Pack. We build a 24/7 emergency-service strategy with real verification signals (license, bonded, in-vehicle photos, dispatch radius pages).",
    bullets: [
      "24/7 emergency-service landing pages with dispatch radius detail",
      "Service-specific pages (auto lockout, house lockout, rekey, safe, commercial)",
      "Neighborhood Map Pack pages (Briargate, Powers, Monument, Fountain)",
      "GBP verification + license-display strategy (spam-resistant ranking)",
      "Review-generation playbook with on-truck QR codes",
      "Backlinks from real Colorado safety + property-management publications",
    ],
  }),
  industrySeoConfig({
    slug: "colorado-springs-pest-control-seo",
    industry: "Pest Control",
    industryLower: "pest control",
    painPoint:
      "Pest control companies in Colorado Springs compete with national franchises spending six figures monthly on Google Ads — and a wave of out-of-state operators chasing every wasp-season search from May through August.",
    specifics:
      "Springs pest control search is hyper-seasonal. Wasps + ants spike May–August, mice + voles spike September–November, spiders run year-round, and bed-bug searches are constant emergencies. The Map Pack rewards seasonal landing pages published 60 days before the season hits. Briargate, Black Forest (heavy spider/mouse), Monument, and Falcon (rural pest pressure) each have separate packs. We build a seasonal + pest-specific content cluster with neighborhood Map Pack pages.",
    bullets: [
      "Seasonal landing pages (wasps in spring, mice in fall, spiders year-round)",
      "Pest-specific service pages (bed bugs, mosquitoes, voles, mice, ants)",
      "Neighborhood Map Pack pages (Briargate, Black Forest, Monument, Falcon)",
      "Quarterly-plan upsell content that compounds recurring revenue",
      "Review-generation playbook timed to first-treatment completion",
      "Backlinks from real Colorado home + property-management publications",
    ],
  }),
  industrySeoConfig({
    slug: "colorado-springs-window-cleaning-seo",
    industry: "Window Cleaning",
    industryLower: "window cleaning",
    painPoint:
      "Window cleaning companies in Colorado Springs face a seasonal lead cliff — spring + fall booking surges are the entire year, and missing the Map Pack in March or September means a thin schedule for months.",
    specifics:
      "Springs window cleaning search peaks twice a year: pre-summer (March–May) and post-summer (September–October). HOA-managed neighborhoods (Briargate, Cordera, Banning Lewis, Monument) drive a disproportionate share of high-ticket residential bookings. Commercial routes (downtown, North Academy) are a separate, less-seasonal stream. We build a seasonal + residential-vs-commercial content cluster with neighborhood Map Pack pages and gutter-cleaning bundle upsells.",
    bullets: [
      "Seasonal landing pages (spring booking, fall booking, HOA color-approved)",
      "Residential vs. commercial keyword cluster segmentation",
      "Neighborhood Map Pack pages (Briargate, Cordera, Banning Lewis, Monument)",
      "Gutter-cleaning + screen-repair bundle upsell content",
      "Review-generation playbook timed to project walkthrough",
      "Backlinks from real Colorado HOA + property-management publications",
    ],
  }),

  industrySeoConfig({
    slug: "colorado-springs-real-estate-agent-seo",
    industry: "Real Estate Agent",
    industryLower: "real estate",
    painPoint:
      "Real estate agents in Colorado Springs compete with Zillow, Realtor.com, and Redfin sitting on top of every search result — plus team-leader brokers spending five figures monthly on Google Ads for the same buyer keywords.",
    specifics:
      "Springs real estate search is dominated by neighborhood-specific buyer intent (\"homes for sale Briargate\", \"Monument CO real estate\") and seller intent (\"sell my house Falcon\"). The portal sites own the broad keywords, but neighborhood-specific long-tail and military-PCS content is winnable. We build a hyper-local neighborhood + PCS content strategy that ranks below the portals but above every other agent in the area.",
    bullets: [
      "Neighborhood buyer-guide pages (Briargate, Monument, Falcon, Black Forest)",
      "Military-PCS relocation content cluster (Fort Carson, Peterson, Academy)",
      "Seller-intent pages (\"sell my house [neighborhood]\")",
      "Review-generation timed to closing day",
      "GBP optimization with licensed-broker + specialization signals",
      "Backlinks from real Colorado real estate + relocation publications",
    ],
  }),
];

// ===== Batch 6 — Additional neighborhood pages (5) =====

const BATCH6_NEIGHBORHOODS: SpringsLandingConfig[] = [
  neighborhoodConfig({
    slug: "colorado-springs-seo-old-colorado-city",
    name: "Old Colorado City",
    zips: ["80904", "80905"],
    driveTime: "15 minutes",
    landmarks: "Bancroft Park, the W Colorado Ave shops, or the Garden of the Gods entrance",
    topIndustries:
      "boutique retail, restaurants, art galleries, tour operators, salons, and home-services contractors serving the historic west side",
  }),
  neighborhoodConfig({
    slug: "colorado-springs-seo-broadmoor",
    name: "Broadmoor",
    zips: ["80906", "80905"],
    driveTime: "20 minutes",
    landmarks: "The Broadmoor resort, Cheyenne Mountain Zoo, or the Country Club of Colorado",
    topIndustries:
      "luxury home services, high-end remodeling, financial advisors, estate attorneys, med-spas, and concierge-tier contractors",
  }),
  neighborhoodConfig({
    slug: "colorado-springs-seo-fountain",
    name: "Fountain",
    zips: ["80817", "80911"],
    driveTime: "25 minutes",
    landmarks: "Fountain-Fort Carson High School, the Mesa Ridge area, or the Fort Carson main gate",
    topIndustries:
      "auto repair, HVAC, plumbing, moving companies (Fort Carson PCS), childcare, and military-family services",
  }),
  neighborhoodConfig({
    slug: "colorado-springs-seo-security-widefield",
    name: "Security-Widefield",
    zips: ["80911", "80925", "80931"],
    driveTime: "25 minutes",
    landmarks: "the Widefield High School corridor, Bradley Rd, or the Powers/Hwy 85 intersection",
    topIndustries:
      "auto repair, HVAC, plumbing, moving companies, restaurants, and home-services contractors serving the south Springs corridor",
  }),
  neighborhoodConfig({
    slug: "colorado-springs-seo-cimarron-hills",
    name: "Cimarron Hills",
    zips: ["80915", "80922"],
    driveTime: "12 minutes",
    landmarks: "the Peterson SFB main gate, Cimarron Hills Golf Course, or the Powers/Galley corridor",
    topIndustries:
      "moving companies (Peterson PCS), auto repair, restaurants, HVAC, plumbing, and military-family services",
  }),
];

// ===== Combined export =====

export const SPRINGS_LANDING_CONFIGS: SpringsLandingConfig[] = [
  ...BATCH2,
  ...BATCH3,
  ...BATCH4,
  ...BATCH5,
  ...BATCH6_INDUSTRIES,
  ...BATCH6_NEIGHBORHOODS,
];

export const SPRINGS_LANDING_SLUGS: string[] = SPRINGS_LANDING_CONFIGS.map(
  (c) => c.slug,
);

export function getSpringsLandingConfig(slug: string): SpringsLandingConfig {
  const found = SPRINGS_LANDING_CONFIGS.find((c) => c.slug === slug);
  if (!found) throw new Error(`No Springs landing config for slug: ${slug}`);
  return found;
}
